import { FC, useEffect, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { ModalConfirmAction } from "components/modals/ModalConfirmAction"
import { TopBar } from "components/navigation/TopBar"

import { RolePermissionMatrix } from "features/role-management/RolePermissionMatrix"

import { useProps } from "contexts/PropsContext"

import { Permission, PERMISSIONS } from "constants/permissions"

import { roleManagementService, RoleRow } from "services/role-management/role-management.service"

const cellKey = (roleId: string, perm: Permission) => `${roleId}:${perm}`

export const RoleManagement: FC = () => {
  const { user } = useProps()
  const { t } = useTranslation("translation", { useSuspense: false })
  const [roles, setRoles] = useState<RoleRow[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [pending, setPending] = useState<Set<string>>(new Set())
  const [createOpen, setCreateOpen] = useState(false)
  const [newName, setNewName] = useState("")
  const [newDescription, setNewDescription] = useState("")
  const [creating, setCreating] = useState(false)
  const [roleToDelete, setRoleToDelete] = useState<RoleRow | null>(null)

  const refresh = async () => {
    const res = await roleManagementService.listRoles()
    if (res) setRoles(res)
  }

  useEffect(() => {
    let alive = true
    ;(async () => {
      setLoading(true)
      await refresh()
      if (alive) setLoading(false)
    })()
    return () => {
      alive = false
    }
  }, [])

  const toggle = async (roleId: string, perm: Permission, currentlyHas: boolean) => {
    if (!roles) return

    // Lock-out guard: don't let an admin remove their
    // own ROLE_MANAGEMENT_EDIT if it would leave nobody able to administer.
    if (currentlyHas && perm === PERMISSIONS.ROLE_MANAGEMENT_EDIT) {
      const others = roles.filter((r) => r.id !== roleId).some((r) => r.permissions.includes(perm))
      const callerRoleId = user?.effective_role?.id ?? user?.role
      if (!others) {
        toast.error(t("roleManagement.lastRoleManagementEdit") as string)
        return
      }
      if (callerRoleId === roleId) {
        toast.error(t("roleManagement.cannotRevokeOwn") as string)
        return
      }
    }

    const key = cellKey(roleId, perm)
    setPending((s) => new Set(s).add(key))

    // Optimistic update
    const prev = roles
    const next = roles.map((r) =>
      r.id === roleId ? { ...r, permissions: currentlyHas ? r.permissions.filter((p) => p !== perm) : [...r.permissions, perm] } : r
    )
    setRoles(next)

    try {
      if (currentlyHas) {
        await roleManagementService.revoke(roleId, perm)
      } else {
        await roleManagementService.grant(roleId, perm)
      }
    } catch {
      setRoles(prev)
    } finally {
      setPending((s) => {
        const n = new Set(s)
        n.delete(key)
        return n
      })
    }
  }

  const handleCreate = async () => {
    const name = newName.trim()
    if (!name) {
      toast.error(t("roleManagement.nameRequired") as string)
      return
    }
    setCreating(true)
    try {
      const created = await roleManagementService.createRole(name, newDescription.trim() || undefined)
      if (created) {
        toast.success(t("roleManagement.createdSuccess", { name: created.name }) as string)
        setNewName("")
        setNewDescription("")
        setCreateOpen(false)
        await refresh()
      }
    } finally {
      setCreating(false)
    }
  }

  const requestDelete = (role: RoleRow) => {
    if (role.is_builtin) return
    if (role.user_count > 0) {
      toast.error(t("roleManagement.usersStillAssigned", { count: role.user_count, name: role.name }) as string)
      return
    }
    setRoleToDelete(role)
  }

  const confirmDelete = async (role: RoleRow) => {
    try {
      await roleManagementService.deleteRole(role.id)
      toast.success(t("roleManagement.deleteSuccess", { name: role.name }) as string)
      await refresh()
    } catch {}
  }

  if (loading || !roles) {
    return (
      <div className="grid w-full gap-8 p-8 pt-0 grid-col-1">
        <TopBar title={t("pages.roleManagement.title")} />
        <p className="text-[rgba(6,7,21,1)] dark:text-white">{t("common.loading")}</p>
      </div>
    )
  }

  return (
    <div className="grid w-full gap-8 p-8 pt-0 grid-col-1">
      <TopBar title={t("pages.roleManagement.title")} />

      <div className="flex justify-end items-center gap-4 flex-wrap">
        <button
          className="btn btn-primary bg-transparent text-[rgba(169,78,206,1)] border-[rgba(169,78,206,0.8)] hover:bg-[rgba(169,78,206,0.7)] hover:border-transparent dark:text-white hover:text-white"
          onClick={() => setCreateOpen(true)}
        >
          {t("roleManagement.newCustomRole")}
        </button>
      </div>

      {createOpen && (
        <div className="rounded-lg border p-4 flex flex-col gap-3 border-gray-200 dark:border-[rgba(255,255,255,0.08)] bg-white dark:bg-[#0C0C1A]">
          <div className="font-semibold">{t("roleManagement.createTitle")}</div>
          <input
            className="input input-bordered w-full max-w-md"
            placeholder={t("roleManagement.rolePlaceholder")}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            disabled={creating}
          />
          <input
            className="input input-bordered w-full max-w-md"
            placeholder={t("roleManagement.descriptionPlaceholder")}
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            disabled={creating}
          />
          <div className="flex gap-2">
            <button className="btn btn-primary" onClick={handleCreate} disabled={creating}>
              {creating ? t("roleManagement.creating") : t("common.create")}
            </button>
            <button className="btn btn-ghost" onClick={() => setCreateOpen(false)} disabled={creating}>
              {t("common.cancel")}
            </button>
          </div>
          <p className="text-xs text-[rgba(93,96,101,1)] dark:text-neutral-400">{t("roleManagement.createHint")}</p>
        </div>
      )}

      <RolePermissionMatrix roles={roles} pending={pending} onToggle={toggle} onDeleteRole={requestDelete} />

      <ModalConfirmAction
        data={roleToDelete}
        handler={() => setRoleToDelete(null)}
        text={
          roleToDelete
            ? `${t("roleManagement.deleteConfirmTitle", { name: roleToDelete.name })} ${t("roleManagement.deleteConfirmText")}`
            : ""
        }
        actionText={t("roleManagement.delete")}
        actionHandler={confirmDelete}
      />
    </div>
  )
}
